<script lang="ts">
  import { page } from '$app/stores';
  import type { Footer } from '../types';
  import type { MktSettings } from 'customer-components/lib/types';
  import { formatPhoneNumberHref } from '../../helpers/phoneNumberHelper';
  import { DisclaimerModal } from 'customer-components/lib/components';

  export let properties: Footer;
  export let settings: MktSettings;
  export let disclaimer: { privacyPolicy: string; termsOfService: string };

  $: ({ description, cities, copyright } = properties);
  $: ({ logo, businessName, phoneNumber } = settings);

  $: {
    if (!properties?.cities) properties.cities = [];
  }

  let isVooltPro = $page.url.searchParams.get('vp');
  let disclaimerModalVisible = false;
  let disclaimerModalContent = { html: '' };

  function openDisclaimer(page: 'privacy-policy' | 'terms-of-use') {
    const html =
      page === 'privacy-policy'
        ? disclaimer?.privacyPolicy
        : disclaimer?.termsOfService;
    disclaimerModalVisible = true;
    disclaimerModalContent = { html };
  }
</script>

<footer
  class="bg-[--color-background] p-[16px] pb-[80px] md:p-[40px] md:pb-[60px]"
>
  <div class="mx-auto flex w-full max-w-[1180px] flex-col gap-[8px]">
    <div
      class="flex flex-col justify-between gap-2 md:flex-row md:items-center"
    >
      <div class="flex flex-1 flex-col gap-[20px] md:flex-row md:items-center">
        {#if logo.value}
          <div class="flex-shrink-0">
            <img
              loading="lazy"
              class="h-full max-h-[56px]"
              src={logo?.value}
              alt="Logo"
            />
          </div>
        {:else}
          <h1 class="h1">{@html businessName}</h1>
        {/if}
        <div class="block flex-shrink-0 md:hidden">
          <a
            class="flex w-full items-center gap-[8px] text-[14px] text-[--color-primary]"
            href="tel:{formatPhoneNumberHref(phoneNumber.value)}"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M17.0725 14.2832L13.685 11.2032C13.5249 11.0577 13.3145 10.98 13.0982 10.9867C12.8819 10.9934 12.6767 11.0839 12.5258 11.239L10.5317 13.2899C10.0517 13.1982 9.08667 12.8974 8.09334 11.9065C7.10001 10.9124 6.79917 9.94487 6.71001 9.46821L8.75917 7.47321C8.91452 7.32248 9.00512 7.11723 9.01181 6.90088C9.0185 6.68453 8.94074 6.47407 8.79501 6.31404L5.71584 2.92737C5.57004 2.76684 5.36741 2.66946 5.15097 2.65592C4.93453 2.64239 4.72134 2.71376 4.55667 2.85487L2.74834 4.40571C2.60427 4.5503 2.51827 4.74275 2.50667 4.94654C2.49417 5.15487 2.25584 10.0899 6.08251 13.9182C9.42084 17.2557 13.6025 17.4999 14.7542 17.4999C14.9225 17.4999 15.0258 17.4949 15.0533 17.4932C15.2571 17.4818 15.4494 17.3954 15.5933 17.2507L17.1433 15.4415C17.285 15.2774 17.3569 15.0644 17.3437 14.848C17.3304 14.6315 17.2331 14.4289 17.0725 14.2832Z"
                fill="var(--color-primary)"
              />
            </svg>
            {phoneNumber.value}
          </a>
        </div>
        <div>
          <p class="text-[14px] md:max-w-[85%]">
            {@html description?.value}
          </p>
        </div>
      </div>

      <div class="hidden flex-shrink-0 md:block">
        <a
          class="flex w-full items-center gap-[8px] text-[14px] text-[--color-primary]"
          href="tel:{formatPhoneNumberHref(phoneNumber.value)}"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M17.0725 14.2832L13.685 11.2032C13.5249 11.0577 13.3145 10.98 13.0982 10.9867C12.8819 10.9934 12.6767 11.0839 12.5258 11.239L10.5317 13.2899C10.0517 13.1982 9.08667 12.8974 8.09334 11.9065C7.10001 10.9124 6.79917 9.94487 6.71001 9.46821L8.75917 7.47321C8.91452 7.32248 9.00512 7.11723 9.01181 6.90088C9.0185 6.68453 8.94074 6.47407 8.79501 6.31404L5.71584 2.92737C5.57004 2.76684 5.36741 2.66946 5.15097 2.65592C4.93453 2.64239 4.72134 2.71376 4.55667 2.85487L2.74834 4.40571C2.60427 4.5503 2.51827 4.74275 2.50667 4.94654C2.49417 5.15487 2.25584 10.0899 6.08251 13.9182C9.42084 17.2557 13.6025 17.4999 14.7542 17.4999C14.9225 17.4999 15.0258 17.4949 15.0533 17.4932C15.2571 17.4818 15.4494 17.3954 15.5933 17.2507L17.1433 15.4415C17.285 15.2774 17.3569 15.0644 17.3437 14.848C17.3304 14.6315 17.2331 14.4289 17.0725 14.2832Z"
              fill="var(--color-primary)"
            />
          </svg>
          {phoneNumber.value}
        </a>
      </div>
    </div>
    {#if cities?.length}
      <div class="pt-[20px] text-[14px]">
        <b class="font-[700]">Proudly Serving:</b>
        {#each cities as city, index}
          <span
            >{city}{cities.length > 1 && index !== cities.length - 1
              ? ', '
              : '.'}</span
          >
        {/each}
      </div>
    {/if}

    <div
      class="text-[rgba(17, 17, 17, 0.64)] flex items-center justify-end gap-2"
    >
      {#if properties?.socials?.facebook}
        <a
          href={properties.socials.facebook}
          target="_blank"
          aria-label="Facebook"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            />
            <path
              d="M12.803 13.0342V18.3533H10.3595V13.0342H8.32959V10.8774H10.3595V10.0926C10.3595 7.17931 11.5765 5.64746 14.1516 5.64746C14.941 5.64746 15.1383 5.77433 15.5706 5.87771V8.01102C15.0866 7.92644 14.9504 7.87945 14.4476 7.87945C13.8508 7.87945 13.5313 8.04861 13.24 8.38223C12.9486 8.71586 12.803 9.29382 12.803 10.1208V10.8821H15.5706L14.8282 13.0389H12.803V13.0342Z"
              fill="white"
            />
          </svg>
        </a>
      {/if}

      {#if properties?.socials?.linkedIn}
        <a
          href={properties.socials.linkedIn}
          target="_blank"
          aria-label="LinkedIn"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            />
            <path
              d="M9.18614 9.50134H6.82617V17.0271H9.18614V9.50134Z"
              fill="white"
            />
            <path
              d="M15.57 9.33829C15.483 9.32741 15.3906 9.32197 15.2981 9.31654C13.9768 9.26216 13.2318 10.0452 12.9708 10.3823C12.9001 10.4748 12.8675 10.5291 12.8675 10.5291V9.52317H10.6108V17.049H12.8675H12.9708C12.9708 16.2822 12.9708 15.521 12.9708 14.7542C12.9708 14.341 12.9708 13.9277 12.9708 13.5144C12.9708 13.0033 12.9327 12.4595 13.1883 11.9919C13.4058 11.6004 13.7973 11.4046 14.2378 11.4046C15.5428 11.4046 15.57 12.5846 15.57 12.6934C15.57 12.6988 15.57 12.7042 15.57 12.7042V17.0816H17.93V12.1713C17.93 10.4911 17.0763 9.50142 15.57 9.33829Z"
              fill="white"
            />
            <path
              d="M8.00555 8.52967C8.76235 8.52967 9.37586 7.91616 9.37586 7.15937C9.37586 6.40257 8.76235 5.78906 8.00555 5.78906C7.24876 5.78906 6.63525 6.40257 6.63525 7.15937C6.63525 7.91616 7.24876 8.52967 8.00555 8.52967Z"
              fill="white"
            />
          </svg>
        </a>
      {/if}

      {#if properties?.socials?.instagram}
        <a
          href={properties?.socials?.instagram}
          target="_blank"
          aria-label="Instagram"
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            />
            <path
              d="M15.2896 16.9401H8.71088C7.80178 16.9401 7.05908 16.1974 7.05908 15.2883V8.70953C7.05908 7.80044 7.80178 7.05774 8.71088 7.05774H15.2896C16.1987 7.05774 16.9414 7.80044 16.9414 8.70953V15.2883C16.9414 16.2015 16.2028 16.9401 15.2896 16.9401Z"
              fill="white"
            />
            <path
              d="M12.0019 14.5389C11.3241 14.5389 10.6869 14.2751 10.208 13.7962C9.72913 13.3173 9.46533 12.6801 9.46533 12.0024C9.46533 11.3246 9.72913 10.6874 10.208 10.2085C10.6869 9.72962 11.3241 9.46582 12.0019 9.46582C12.6796 9.46582 13.3168 9.72962 13.7957 10.2085C14.2746 10.6874 14.5384 11.3246 14.5384 12.0024C14.5384 12.6801 14.2746 13.3173 13.7957 13.7962C13.3128 14.2751 12.6796 14.5389 12.0019 14.5389ZM12.0019 10.0056C10.902 10.0056 10.0051 10.8985 10.0051 12.0024C10.0051 13.1022 10.898 13.9991 12.0019 13.9991C13.1017 13.9991 13.9986 13.1063 13.9986 12.0024C13.9946 10.9025 13.1017 10.0056 12.0019 10.0056Z"
              fill="#8D939A"
            />
            <path
              d="M15.0324 9.38214C15.3014 9.38214 15.5195 9.16409 15.5195 8.89511C15.5195 8.62613 15.3014 8.40808 15.0324 8.40808C14.7635 8.40808 14.5454 8.62613 14.5454 8.89511C14.5454 9.16409 14.7635 9.38214 15.0324 9.38214Z"
              fill="#8D939A"
            />
          </svg>
        </a>
      {/if}

      {#if properties?.socials?.twitter}
        <a
          href={properties.socials.twitter}
          target="_blank"
          aria-label="Twitter"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            />
            <path
              d="M6.77588 15.6653C7.99486 15.7339 9.0608 15.4489 10.1004 14.6943C9.05025 14.5149 8.32203 14.0347 7.92626 13.0426C8.23232 13.0004 8.50145 13.0796 8.81806 12.9529C7.78378 12.4991 7.11361 11.8184 7.08722 10.6416C7.41439 10.668 7.66241 10.8843 8.05291 10.8527C7.10305 9.92394 6.81282 8.91076 7.40911 7.68123C8.40118 8.82105 9.56212 9.59676 10.9816 9.96088C11.0608 9.98198 11.1346 10.0031 11.2138 10.0189C11.5779 10.1034 12.0106 10.2828 12.2323 10.2564C12.6122 10.2089 12.2323 9.7709 12.3272 9.20099C12.628 7.42794 14.5805 6.62056 16.0264 7.66012C16.4485 7.96618 16.781 7.96091 17.1979 7.77621C17.4142 7.68123 17.6359 7.58624 17.8944 7.47543C17.8364 7.98202 17.4459 8.26169 17.1345 8.63636C17.4881 8.71551 17.7731 8.61525 18.1002 8.50971C17.9894 8.87382 17.715 9.07962 17.467 9.28015C17.2084 9.48595 17.1082 9.7023 17.0976 10.04C16.9393 15.1534 11.1294 19.1058 7.15582 15.9608C6.77061 15.6547 7.14527 15.9608 6.77588 15.6653Z"
              fill="white"
            />
          </svg>
        </a>
      {/if}

      {#if properties?.socials?.youtube}
        <a
          href={properties.socials.youtube}
          target="_blank"
          aria-label="YouTube"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            />
            <path
              d="M16.5839 16.517H7.54789C6.33884 16.517 5.36475 15.4477 5.36475 14.1325V9.86615C5.36475 8.54561 6.34374 7.48169 7.54789 7.48169H16.5839C17.793 7.48169 18.7671 8.55095 18.7671 9.86615V14.1325C18.772 15.4531 17.793 16.517 16.5839 16.517Z"
              fill="white"
            />
            <path
              d="M14.436 11.9331L10.6348 9.74109V14.1251L14.436 11.9331Z"
              fill="#8D939A"
            />
          </svg>
        </a>
      {/if}

      {#if properties?.socials?.tiktok}
        <a href={properties.socials.tiktok} target="_blank" aria-label="TikTok">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            />
            <path
              d="M16.4276 8.90246C15.8461 8.77459 15.3201 8.4658 14.925 8.0204C14.5299 7.575 14.286 7.01588 14.2284 6.42329V6.16663H12.2159V14.1408C12.2163 14.4946 12.1054 14.8396 11.8988 15.1269C11.6923 15.4142 11.4006 15.6292 11.065 15.7414C10.7295 15.8537 10.3671 15.8576 10.0293 15.7525C9.69143 15.6474 9.39521 15.4386 9.18258 15.1558C8.9921 14.9054 8.87545 14.6068 8.84584 14.2936C8.81623 13.9804 8.87484 13.6652 9.01504 13.3836C9.15524 13.102 9.37143 12.8653 9.63917 12.7001C9.90691 12.535 10.2155 12.448 10.5301 12.4491C10.7041 12.4481 10.8772 12.4737 11.0434 12.525V10.4833C10.8499 10.4593 10.655 10.4496 10.4601 10.4541C9.74218 10.4733 9.04545 10.7014 8.45522 11.1105C7.86498 11.5196 7.40685 12.092 7.13694 12.7575C6.86702 13.423 6.79703 14.1528 6.93554 14.8575C7.07404 15.5621 7.41503 16.2111 7.91675 16.725C8.43095 17.2477 9.08896 17.6059 9.80716 17.7538C10.5254 17.9018 11.2713 17.8329 11.9503 17.556C12.6293 17.279 13.2106 16.8065 13.6204 16.1984C14.0302 15.5903 14.2499 14.8741 14.2518 14.1408V10.0575C15.0634 10.6373 16.0368 10.9474 17.0343 10.9441V8.96079C16.8383 8.9616 16.6428 8.94205 16.4509 8.90246H16.4276Z"
              fill="white"
            />
          </svg>
        </a>
      {/if}

      {#if properties?.socials?.googleMyBusiness}
        <a
          href={properties.socials.googleMyBusiness}
          target="_blank"
          aria-label="Google My Business"
        >
          <svg
            id="a"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            ><circle
              cx="12"
              cy="12"
              r="12"
              fill="var(--color-accent, #8dc63f)"
            /><g id="b"
              ><g id="c"
                ><g id="d"
                  ><rect
                    x="4.75"
                    y="4.75"
                    width="14.51"
                    height="14.51"
                    fill="none"
                  /><rect
                    x="4.75"
                    y="4.75"
                    width="14.51"
                    height="14.51"
                    fill="none"
                  /></g
                ><g id="e"
                  ><path
                    d="M12.21,10.94v2.3h3.29c-.41,1.47-1.77,2.47-3.29,2.42-2.02,0-3.65-1.65-3.65-3.67,0-2.02,1.65-3.65,3.67-3.65.87,0,1.7.31,2.36.88l1.69-1.69c-2.47-2.25-6.29-2.06-8.54.41-2.25,2.47-2.06,6.29.41,8.54,1.11,1.01,2.55,1.57,4.05,1.57,5.08,0,6.2-4.75,5.71-7.1h-5.71Z"
                    fill="#fff"
                  /></g
                ></g
              ></g
            ></svg
          >
        </a>
      {/if}
    </div>

    <div
      class="border-[rgba(17, 17, 17, 0.07)] mt-[16px] flex flex-col-reverse justify-between gap-[16px] border-t pt-[28px] md:mt-[20px] md:flex-row md:items-center"
    >
      <div class="text-[rgba(17, 17, 17, 0.64)] text-[14px]">
        {#if copyright?.value?.replace(/<[^>]*>/g, '')?.length}
          {@html copyright?.value}
        {:else}
          © Copyright {businessName} 2024 All rights reserved
        {/if}
      </div>

      <div class="privacy-terms">
        <div class="flex gap-[16px]">
          {#if disclaimer}
            <button
              class="whitespace-nowrap text-[14px]"
              on:click={() => openDisclaimer('privacy-policy')}
              >Privacy Policy</button
            >
            <button
              class="whitespace-nowrap text-[14px]"
              on:click={() => openDisclaimer('privacy-policy')}
              >Terms of Use</button
            >
          {:else}
            <a
              class="whitespace-nowrap text-[14px]"
              href="/privacy-policy/"
              target="_blank">Privacy Policy</a
            >
            <a
              class="whitespace-nowrap text-[14px]"
              href="/terms-of-use/"
              target="_blank">Terms of Use</a
            >
          {/if}
          {#if isVooltPro}
            <a
              class="text-small"
              href="https://www.voolt.pro/ref={isVooltPro}"
              target="_blank">Voolt.Pro Verified</a
            >
          {/if}
        </div>
      </div>
    </div>
  </div>
</footer>

{#if disclaimerModalVisible}
  <DisclaimerModal
    {disclaimerModalContent}
    on:close={() => (disclaimerModalVisible = false)}
  />
{/if}
